import { useEffect, useState } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { supabase } from './supabaseClient'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import ListingManagementSection from './ListingManagementSection'

export default function ListingManagement() {
  const { t } = useTranslation()
  const { listingId } = useParams()
  const [searchParams] = useSearchParams()
  const notificationId = searchParams.get('notificationId')
  const navigate = useNavigate()

  const [listing, setListing] = useState(null)
  const [user, setUser] = useState(null)
  const [userToken, setUserToken] = useState(null)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [events, setEvents] = useState([])

  // ============================
  //       Fetch Data
  // ============================
  async function fetchData() {
    setLoading(true)

    try {
      // 1. Fetch user from Supabase
      const { data: userData, error: userError } = await supabase.auth.getUser()
      if (userError) console.error('Error fetching user:', userError)
      setUser(userData?.user || null)

      // 2. Fetch user token (if needed for your backend calls)
      const { data: tokenData, error: tokenError } = await supabase.auth.getSession()
      if (tokenError) console.error('Error fetching token:', tokenError)
      setUserToken(tokenData?.session?.access_token || null)
      setSession(tokenData?.session || null)

      // 3. Fetch listing details
      const { data: listingData, error: listingError } = await supabase
        .from('listings')
        .select(
          `*,
            viewings(id, status, viewing_date, user_id),
            offers(id, offer_price, status, user_id)
            listing_engagements(id, liked)`
        )
        .eq('id', listingId)
        .single()

      if (listingError) {
        console.error('Error fetching listing:', listingError)
        setListing(null)
      } else {
        setListing(listingData)
      }

      // 4. Fetch timeline events
      const { data: eventsData, error: eventsError } = await supabase
        .from('listing_events')
        .select('*')
        .eq('listing_id', listingId)
        .order('event_timestamp', { ascending: true })

      if (eventsError) {
        console.error('Error fetching events:', eventsError)
        setEvents([])
      } else {
        setEvents(eventsData || [])
      }
    } catch (error) {
      console.error('Error in fetchData:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId])

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!listing) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>{t('listingManagement.listingNotFound', 'Listing not found.')}</Typography>
      </Box>
    )
  }

  return (
    <ListingManagementSection
      listing={listing}
      events={events}
      refreshData={fetchData}
      user={user}
      userToken={userToken}
      session={session}
    />
  )
}
